import { DesignerTryItOut, type DesignerTrySnapshot } from "./DesignerTryItOut";
import { compactNavigationQuery } from "./responsive";
import { AccountMenu, useWorkspaceAccount } from "./AccountMenu";
import { DocumentOperationProgress } from "./DocumentOperationProgress";
import { createPortal } from "react-dom";
import { OutputProfileMerge } from "./OutputProfileMerge";
import {
  useWorkspaceSelector,
  useWorkspaceBreadcrumb,
} from "./WorkspaceSelector";
import { readPageLink, pageLinkHash } from "./page-links";
import {
  FieldPresetLibrary,
  VariableTypeMapper,
  friendlyMessage,
  looksLikeError,
  JsonImportButton,
} from "@proof/authoring";
import { WorkspaceRestore } from "./WorkspaceRestore";
import { TenantAccess } from "./TenantAccess";
import { useDocumentDefaults } from "./setup-defaults";
import { TenantSetup } from "./TenantSetup";
import type { SetupView } from "@proof/core";
import {
  AuthoringServicesProvider,
  GlobalProfiles,
  GlobalProfileGrid,
} from "@proof/authoring";
import { LibraryIntegrations } from "./LibraryIntegrations";
import { ProjectProfiles } from "./ProjectProfiles";
import {
  AppearanceSettings,
  DisplayModeToggle,
  ModuleThemeStyle,
} from "./Appearance";
import {
  resolveModuleTheme,
  type AppearanceSettings as AppearanceSettingsValue,
} from "@proof/core";
import { CatalogManager } from "./CatalogManager";
import { DatasetManager } from "./Datasets";
import { ResourceMapping } from "./ResourceMapping";
import { AssetLibrary } from "./AssetLibrary";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  type RefObject,
} from "react";
import { prepareImpersonation } from "./impersonation-session";
import { ImpersonationPicker, type PreviewUser } from "./ImpersonationPicker";
import {
  LayoutDashboard,
  SlidersHorizontal,
  PenTool,
  ScanEye,
  Printer,
  Rows3,
  Plug,
  BookOpen,
  FolderOpen,
  Plus,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Cloud,
  ArrowUpRight,
  MoreHorizontal,
  FileText,
  Layers,
  Save,
  Upload,
  History,
  Settings2,
  Server,
  LogOut,
  X,
  Download,
  Boxes,
  LibraryBig,
  UserRound,
  Users,
  CircleAlert,
  LayoutGrid,
} from "lucide-react";
import {
  captureTextStyle,
  applyTextStyleSnapshot,
  BrowserRepository,
  demoProjects,
  uid,
  newPage,
  outputSchema,
  createOutputProfiles,
  migrateOutputProfiles,
  retainOutputPresets,
  globalOutputSettings,
  retainProductionPresets,
  parseProject,
  createScene,
  type Project,
  type Values,
  type Revision,
  type Resource,
  type Repository,
} from "@proof/core";
import { ProofCanvas } from "@proof/ui";
import { profileConflictMessage, profileSectionConflicts } from "@proof/core";
import { workspaceModuleAllowed } from "./workspace-permissions";
import { Configuration, ReusableFormsModule } from "./Configuration";
import { Designer } from "./Designer";
import { Proof } from "./Proof";
import { ProfileCollections } from "./ProfileCollections";
import { Help } from "./Help";
import { helpActions, type HelpActionId } from "./help-content";
import { Outputs } from "./Outputs";
import { Bulk } from "./Bulk";
import { FontLibrary } from "./FontLibrary";
import { DocumentImport } from "./DocumentImport";
import { DocumentRevision } from "./DocumentRevision";
import { ProjectSummary } from "./ProjectSummary";
import { ProjectPackage } from "./ProjectPackage";
import { Button, Control, Modal } from "./components";
import { downloadJson, type JobView } from "./api";
import {
  WorkspaceTransportProvider,
  useWorkspaceTransport,
} from "./WorkspaceContext";
import type { WorkspaceTransport } from "./workspace-transport";
import { PublishedWorkspaceView } from "./PublishedWorkspaceView";
import { ProjectsHome } from "./ProjectsHome";
import { ProjectSettings } from "./ProjectSettings";
export interface AppWorkspace {
  repository: Repository;
  transport: WorkspaceTransport;
  onLeave?: () => void;
  onIdentityChanged?: () => void;
  registerBeforeLeave?: (handler: () => Promise<void>) => () => void;
  displayName?: string;
}
// Pages that belong to the open project; they appear nested under it.
const projectModules = [
  { id: "overview", name: "Overview", Icon: LayoutDashboard },
  { id: "summary", name: "Project summary", Icon: FileText },
  { id: "configuration", name: "Fields & variables", Icon: SlidersHorizontal },
  { id: "reusable-forms", name: "Reusable forms", Icon: SlidersHorizontal },
  { id: "designer", name: "Document designer", Icon: PenTool },
  { id: "proof", name: "Live proof", Icon: ScanEye },
  { id: "bulk", name: "Bulk generation", Icon: Rows3 },
];
// Company/site-wide building blocks live in the shared library.
const resourcePages = [
  { key: "assets", name: "Assets" },
  { key: "fonts", name: "Fonts" },
  { key: "forms", name: "Forms" },
  { key: "profiles", name: "Global profiles" },
  { key: "profile-data", name: "Global profile data" },
  { key: "validation", name: "Validation presets" },
  { key: "formatting", name: "Formatting presets" },
  { key: "variable-types", name: "Variable data types" },
  { key: "datasets", name: "Datasets" },
  { key: "mapping", name: "Mapping" },
  { key: "bleed", name: "Page bleed profiles" },
  { key: "sheets", name: "Sheets & paper" },
  { key: "outputs", name: "Output profiles" },
  { key: "integrations", name: "Integrations" },
  { key: "appearance", name: "Appearance & styles" },
];
// A project's own configuration includes assignments from shared catalogs.
const projectResourceKeys = [
  "validation",
  "formatting",
  "variable-types",
  "assets",
  "forms",
  "profiles",
  "datasets",
  "mapping",
  "bleed",
  "sheets",
  "outputs",
];
const projectResourcePages = resourcePages
  .filter((page) => projectResourceKeys.includes(page.key))
  .map((page) =>
    page.key === "profiles" ? { ...page, name: "Profiles" } : page,
  );
const projectResource = (key: string) =>
  key === "outputs" ? "outputs" : `resources-${key}`;
const resourceKey = (module: string) =>
  module === "outputs" || module === "library-outputs"
    ? "outputs"
    : module.replace(/^(library|resources)-?/, "") || "all";
const isProjectModule = (module: string) =>
  projectModules.some((m) => m.id === module) ||
  module === "settings" ||
  module === "outputs" ||
  module === "resources" ||
  module.startsWith("resources-");
const workspacePages: Record<string, string> = {
  projects: "Projects",
  profiles: "Your profiles",
  library: "Configuration",
  access: "Users & roles",
  setup: "Workspace setup",
  operations: "Render operations",
  help: "Guides & help",
};
/** Sub-pages of a sidebar group; inline on desktop, a flyout when compact. */
function ResourceMenu({
  id,
  label,
  menuRef,
  open,
  pages,
  active,
  onSelect,
  onClose,
}: {
  id: string;
  label: string;
  menuRef: RefObject<HTMLElement | null>;
  open: boolean;
  pages: { key: string; name: string }[];
  active: (key: string) => boolean;
  onSelect: (key: string) => void;
  onClose: () => void;
}) {
  return (
    <nav
      ref={menuRef}
      id={id}
      className="resource-submenus"
      aria-label={label}
      hidden={!open}
    >
      <div className="configuration-menu-heading">
        <strong>{label}</strong>
        <button
          type="button"
          aria-label={`Close ${label.toLowerCase()} menu`}
          onClick={onClose}
        >
          <X size={16} />
        </button>
      </div>
      {pages.map((page) => (
        <button
          key={page.key}
          className={active(page.key) ? "active" : ""}
          aria-current={active(page.key) ? "page" : undefined}
          onClick={() => onSelect(page.key)}
        >
          {page.name}
        </button>
      ))}
    </nav>
  );
}
/** Closes a compact-sidebar flyout on outside pointer/focus or Escape. */
function useFlyoutDismissal(
  open: boolean,
  menu: RefObject<HTMLElement | null>,
  button: RefObject<HTMLButtonElement | null>,
  close: () => void,
) {
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent | FocusEvent) => {
      if (
        event.target instanceof Node &&
        !menu.current?.contains(event.target) &&
        !button.current?.contains(event.target)
      )
        close();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      close();
      button.current?.focus();
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("focusin", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("focusin", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);
}
export function App({ workspace }: { workspace?: AppWorkspace } = {}) {
  const inherited = useWorkspaceTransport();
  const [local] = useState(() =>
    workspace
      ? undefined
      : new BrowserRepository("proof-studio-workspace-v1", demoProjects()),
  );
  const transport = workspace?.transport || inherited;
  return (
    <WorkspaceTransportProvider value={transport}>
      <Studio
        repository={workspace?.repository || local!}
        onLeave={workspace?.onLeave}
        onIdentityChanged={workspace?.onIdentityChanged}
        registerBeforeLeave={workspace?.registerBeforeLeave}
        displayName={workspace?.displayName}
      />
    </WorkspaceTransportProvider>
  );
}
function Studio({
  repository,
  onLeave,
  onIdentityChanged,
  registerBeforeLeave,
  displayName,
}: {
  repository: Repository;
  onLeave?: () => void;
  onIdentityChanged?: () => void;
  registerBeforeLeave?: (handler: () => Promise<void>) => () => void;
  displayName?: string;
}) {
  const [outputCatalogRevision, setOutputCatalogRevision] = useState(0);
  const companySelector = useWorkspaceSelector();
  const workspaceAccount = useWorkspaceAccount();
  const breadcrumbHost = useWorkspaceBreadcrumb();
  const transport = useWorkspaceTransport(),
    { api, upload } = transport;
  const tenantId = transport.principal?.tenantId || "demo";
  const pagedWorkspace = !transport.demo && !!repository.page;
  const [previewUsers, setPreviewUsers] = useState<PreviewUser[]>([]);
  const [checkingPreviewUsers, setCheckingPreviewUsers] = useState(false);
  const [choosingPreviewUser, setChoosingPreviewUser] = useState(false);
  const [designerTry, setDesignerTry] = useState<DesignerTrySnapshot>();
  const previewLoad = useRef<AbortController | null>(null);
  const previewOpener = useRef<HTMLElement | null>(null);
  const launchPreview = useRef<((subject: string | null) => void) | null>(null);
  const [linkReady, setLinkReady] = useState(false);
  const linkNavigating = useRef(false);
  const pendingPart = useRef<string | undefined>(undefined);
  const documentOperation = useRef<
    { kind: "save" | "publish"; url: string } | undefined
  >(undefined);
  const [documentOperationKind, setDocumentOperationKind] = useState<
    "save" | "publish"
  >();
  const beginDocumentOperation = (kind: "save" | "publish") => {
    if (documentOperation.current) return false;
    documentOperation.current = { kind, url: location.href };
    setDocumentOperationKind(kind);
    return true;
  };
  const finishDocumentOperation = () => {
    documentOperation.current = undefined;
    setDocumentOperationKind(undefined);
  };
  const [loading, setLoading] = useState(true),
    [loadError, setLoadError] = useState("");
  const [projects, setProjects] = useState<Project[]>([]),
    [selectedId, setSelected] = useState(
      () =>
        (transport.demo
          ? localStorage.getItem("proof-studio-current-document")
          : null) || "botanical",
    ),
    // Whether the selected project is open; closed shows the Projects page.
    [inProject, setInProject] = useState(
      () =>
        transport.demo &&
        transport.can("tenant.documents.view") &&
        localStorage.getItem("proof-studio-project-open") === "1",
    ),
    [module, setModuleState] = useState(() =>
      !transport.can("tenant.documents.view")
        ? "library-assets"
        : transport.demo &&
            localStorage.getItem("proof-studio-project-open") === "1"
          ? "overview"
          : "projects",
    ),
    [compactSidebar, setCompactSidebar] = useState(
      () => matchMedia(compactNavigationQuery).matches,
    ),
    [resourcesOpen, setResourcesOpen] = useState(false),
    [libraryOpen, setLibraryOpen] = useState(false),
    [projectsOpen, setProjectsOpen] = useState(false),
    [recentHistory, setRecentHistory] = useState<{
      scope: string;
      ids: string[];
    }>({ scope: "", ids: [] }),
    [draft, setDraft] = useState<Project>(),
    [dirty, setDirty] = useState(false),
    [history, setHistory] = useState<Project[]>([]),
    [future, setFuture] = useState<Project[]>([]),
    [toast, setToast] = useState(""),
    [values, setValues] = useState<Values>({}),
    [sourceParameters, setSourceParameters] = useState<
      import("@proof/core").SourceParameters
    >({}),
    [part, setPart] = useState(""),
    [demoProfile, setDemoProfile] = useState<"alex" | "jamie" | "guest">(
      "alex",
    ),
    [dialog, setDialog] = useState(""),
    [replaceRequest, setReplaceRequest] = useState<{
      names: string[];
      resolve: (choice: "replace" | "copy" | "cancel") => void;
    }>(),
    [revisionRows, setRevisions] = useState<Revision[]>([]),
    [title, setTitle] = useState(""),
    [search, setSearch] = useState("");
  const completeCatalog = useRef(!pagedWorkspace);
  const keepFullCatalog = useRef(false);
  const retainedProjectIds = useRef<Set<string>>(new Set());
  retainedProjectIds.current = new Set([selectedId, ...recentHistory.ids]);
  const [catalogRevision, setCatalogRevision] = useState(0);
  const [catalogFailure, setCatalogFailure] = useState("");
  const [catalogAttempt, setCatalogAttempt] = useState(0);
  const [latestProjectIds, setLatestProjectIds] = useState<string[]>([]);
  const cacheProjects = useCallback(
    (rows: Project[]) => {
      if (pagedWorkspace && !keepFullCatalog.current)
        completeCatalog.current = false;
      setProjects((current) => {
        const merged = new Map(
          [...current, ...rows].map((project) => [project.id, project]),
        );
        if (!pagedWorkspace || keepFullCatalog.current)
          return [...merged.values()];
        const keep = new Set([
          ...retainedProjectIds.current,
          ...rows.map((project) => project.id),
        ]);
        const retained = [...merged.values()].filter((project) =>
          keep.has(project.id),
        );
        const capacity = Math.max(0, 100 - retained.length);
        const remaining = capacity
          ? [...merged.values()]
              .filter((project) => !keep.has(project.id))
              .slice(-capacity)
          : [];
        return [...remaining, ...retained];
      });
    },
    [pagedWorkspace],
  );
  const needsCatalog = pagedWorkspace && module === "profiles" && !inProject;
  keepFullCatalog.current = needsCatalog;
  useEffect(() => {
    if (!needsCatalog || completeCatalog.current) return;
    let active = true;
    setCatalogFailure("");
    void repository
      .list(tenantId)
      .then((rows) => {
        if (active) {
          completeCatalog.current = true;
          setProjects(rows);
        }
      })
      .catch((cause) => {
        if (active)
          setCatalogFailure(
            cause instanceof Error
              ? cause.message
              : "The project catalog could not be loaded.",
          );
      });
    return () => {
      active = false;
    };
  }, [needsCatalog, module, repository, tenantId, catalogAttempt]);
  useEffect(() => {
    if (!pagedWorkspace || !projectsOpen || !repository.page) return;
    let active = true;
    void Promise.all([
      repository.page(tenantId, { limit: 10, order: "newest" }),
      Promise.all(recentHistory.ids.map((id) => repository.get(tenantId, id))),
    ])
      .then(([latest, recent]) => {
        if (active) {
          setLatestProjectIds(latest.items.map((project) => project.id));
          cacheProjects([
            ...latest.items,
            ...recent.filter((project): project is Project => !!project),
          ]);
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [
    pagedWorkspace,
    projectsOpen,
    repository,
    tenantId,
    recentHistory.ids,
    cacheProjects,
  ]);
  const allowed = (name: string) => workspaceModuleAllowed(transport, name);
  useEffect(() => {
    setCheckingPreviewUsers(false);
    setChoosingPreviewUser(false);
    return () => previewLoad.current?.abort();
  }, [draft?.id, selectedId, transport]);
  const configurationButton = useRef<HTMLButtonElement>(null);
  const configurationMenu = useRef<HTMLElement>(null);
  const libraryButton = useRef<HTMLButtonElement>(null);
  const libraryMenu = useRef<HTMLElement>(null);
  const projectsButton = useRef<HTMLButtonElement>(null);
  const projectsMenu = useRef<HTMLElement>(null);
  const recentScope = `proof-studio-recent-projects:${encodeURIComponent(tenantId)}:${encodeURIComponent(transport.principal?.subject || "demo")}`;
  useEffect(() => {
    let ids: string[] = [];
    try {
      const stored: unknown = JSON.parse(
        localStorage.getItem(recentScope) || "[]",
      );
      if (Array.isArray(stored))
        ids = [
          ...new Set(
            stored.filter((id): id is string => typeof id === "string"),
          ),
        ].slice(0, 10);
    } catch {}
    setRecentHistory({ scope: recentScope, ids });
  }, [recentScope]);
  useEffect(() => {
    if (
      recentHistory.scope !== recentScope ||
      !inProject ||
      !projects.some((p) => p.id === selectedId) ||
      recentHistory.ids[0] === selectedId
    )
      return;
    const ids = [
      selectedId,
      ...recentHistory.ids.filter((id) => id !== selectedId),
    ].slice(0, 10);
    setRecentHistory({ scope: recentScope, ids });
    try {
      localStorage.setItem(recentScope, JSON.stringify(ids));
    } catch {}
  }, [recentScope, recentHistory, selectedId, inProject, projects]);
  const recentProjects = useMemo(() => {
    const available = new Map(
      projects
        .filter((project) => project.status !== "archived")
        .map((project) => [project.id, project]),
    );
    const ids = [
      ...new Set([
        ...(recentHistory.scope === recentScope ? recentHistory.ids : []),
        ...latestProjectIds,
        ...projects
          .slice()
          .reverse()
          .map((project) => project.id),
      ]),
    ];
    return ids
      .flatMap((id) => (available.has(id) ? [available.get(id)!] : []))
      .slice(0, 10);
  }, [projects, recentHistory, recentScope, latestProjectIds]);
  useEffect(() => {
    const media = matchMedia(compactNavigationQuery);
    const change = () => {
      setCompactSidebar(media.matches);
      setResourcesOpen(false);
      setLibraryOpen(false);
      setProjectsOpen(false);
    };
    media.addEventListener("change", change);
    return () => media.removeEventListener("change", change);
  }, []);
  useFlyoutDismissal(
    compactSidebar && resourcesOpen,
    configurationMenu,
    configurationButton,
    () => setResourcesOpen(false),
  );
  useFlyoutDismissal(
    compactSidebar && libraryOpen,
    libraryMenu,
    libraryButton,
    () => setLibraryOpen(false),
  );
  useFlyoutDismissal(
    compactSidebar && projectsOpen,
    projectsMenu,
    projectsButton,
    () => setProjectsOpen(false),
  );
  useEffect(() => {
    if (transport.demo)
      localStorage.setItem("proof-studio-project-open", inProject ? "1" : "");
  }, [inProject]);
  const setupChecked = useRef(false);
  // Project pages need an open project; library pages fall back to the
  // shared library and everything else returns to the Projects page.
  const fromRecentProjects = useRef(false);
  const setModule = (name: string, projectOpen = inProject) => {
    if (documentOperation.current) return;
    let target = name;
    if (!projectOpen && isProjectModule(target)) {
      if (
        target === "outputs" ||
        target === "resources" ||
        target.startsWith("resources-")
      )
        target =
          target === "outputs"
            ? "library-outputs"
            : target.replace("resources", "library");
      else {
        if (!allowed("projects")) {
          setToast("This workspace action is not available for this account.");
          return;
        }
        setupChecked.current = true;
        setModuleState("projects");
        setToast("Open a project first.");
        return;
      }
    }
    if (allowed(target)) {
      setupChecked.current = true;
      // Selecting a destination collapses menu sections it does not belong to;
      // choosing a project from the open recent-projects list keeps it open.
      if (!fromRecentProjects.current) setProjectsOpen(false);
      fromRecentProjects.current = false;
      if (!(target.startsWith("resources") || target === "outputs"))
        setResourcesOpen(false);
      if (!target.startsWith("library")) setLibraryOpen(false);
      setModuleState(target);
    } else setToast("This workspace action is not available for this account.");
  };
  // Only one expandable navigation section is open at a time.
  useEffect(() => {
    if (!projectsOpen) return;
    setResourcesOpen(false);
    setLibraryOpen(false);
  }, [projectsOpen]);
  useEffect(() => {
    if (!resourcesOpen) return;
    setProjectsOpen(false);
    setLibraryOpen(false);
  }, [resourcesOpen]);
  useEffect(() => {
    if (!libraryOpen) return;
    setProjectsOpen(false);
    setResourcesOpen(false);
  }, [libraryOpen]);
  const scratch = useMemo(
    () =>
      parseProject({
        schemaVersion: 1,
        id: "workspace-resource-context",
        tenantId,
        name: "Workspace defaults",
        fields: [],
        pages: [newPage()],
        outputs: [
          outputSchema.parse({
            id: "workspace-output",
            name: "Print PDF",
            format: "pdf",
          }),
        ],
      }),
    [tenantId],
  );
  const [conflict, setConflict] = useState(false);
  const [previewSelection, setPreviewSelection] = useState<{
    projectId: string;
    versionId: string;
  }>();
  const switching = useRef(false);
  const [setupView, setSetupView] = useState<SetupView>();
  const [appearance, setAppearance] = useState<AppearanceSettingsValue>();
  useEffect(() => {
    let active = true;
    void repository
      .tenantSettings(tenantId)
      .then((settings) => active && setAppearance(settings.appearance))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [repository, tenantId]);
  const acceptSetupView = useCallback((view: SetupView) => {
    setSetupView((current) =>
      !current ||
      current.envelope.tenantId !== view.envelope.tenantId ||
      view.envelope.revision >= current.envelope.revision
        ? view
        : current,
    );
  }, []);
  useEffect(() => {
    let active = true;
    if (!transport.can("tenant.setup.manage")) return;
    void api<SetupView>("/setup")
      .then((view) => {
        if (!active) return;
        acceptSetupView(view);
        if (
          !setupChecked.current &&
          (view.needsInitialSetup || location.pathname === "/admin/setup")
        )
          setModuleState("setup");
        setupChecked.current = true;
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [module, acceptSetupView]);
  const notify = useCallback((s: string) => {
    setToast(s);
  }, []);
  useEffect(() => {
    if (transport.demo)
      localStorage.setItem("proof-studio-current-document", selectedId);
  }, [selectedId]);
  useEffect(() => {
    let current = true;
    if (!transport.can("tenant.documents.view")) {
      setLoading(false);
      return;
    }
    if (pagedWorkspace) {
      setLoading(false);
      return;
    }
    void repository
      .list(tenantId)
      .then((rows) => {
        if (current) {
          setProjects(rows);
          setLoading(false);
        }
      })
      .catch((error) => {
        if (current) {
          setLoadError(error.message);
          setLoading(false);
        }
      });
    return () => {
      current = false;
    };
  }, []);
  useEffect(() => {
    if (projects.length && !projects.some((p) => p.id === selectedId))
      setSelected(projects[0].id);
  }, [projects, selectedId]);
  useEffect(() => {
    if (!toast) return;
    // Problems stay up longer: people need time to read what to do next.
    const timer = setTimeout(
      () => setToast(""),
      looksLikeError(toast) ? 12000 : 6000,
    );
    return () => clearTimeout(timer);
  }, [toast]);
  useEffect(() => {
    const p = projects.find((p) => p.id === selectedId);
    if (p && p.id !== draft?.id) {
      setSourceParameters({});
      setDraft(structuredClone(p));
      setDirty(false);
      setConflict(false);
      setHistory([]);
      setFuture([]);
      setPart(pendingPart.current || "");
      pendingPart.current = undefined;
      try {
        setValues(
          transport.demo
            ? JSON.parse(localStorage.getItem(`proof-values-${p.id}`) || "{}")
            : {},
        );
      } catch {
        setValues({});
      }
    }
  }, [selectedId, projects]);
  const change = (p: Project) => {
    if (!transport.can("tenant.documents.manage")) {
      notify("Document editing is not available for this account.");
      return;
    }
    try {
      const valid = parseProject(p);
      if (draft) setHistory((h) => [...h.slice(-49), draft]);
      setFuture([]);
      setDraft(valid);
      setDirty(true);
    } catch (e) {
      notify((e as Error).message);
    }
  };
  const save = async () => {
    if (!draft) return;
    const profileConflicts = profileSectionConflicts(draft);
    if (profileConflicts.length) {
      notify(
        `Resolve the global profile sections before saving: ${profileConflictMessage(profileConflicts)}. Move variables so each section uses one profile.`,
      );
      return;
    }
    if (!beginDocumentOperation("save")) return;
    try {
      const saved = await repository.save(tenantId, draft, draft.revision);
      setProjects((current) =>
        current.map((p) => (p.id === saved.id ? saved : p)),
      );
      setDraft(saved);
      setDirty(false);
      await syncProject(saved);
      setConflict(false);
      notify(
        transport.demo
          ? "Draft saved in this browser."
          : "Draft saved to workspace.",
      );
    } catch (e) {
      setDirty(true);
      if ((e as { status?: number }).status === 409) setConflict(true);
      notify((e as Error).message);
    } finally {
      finishDocumentOperation();
    }
  };
  const publish = async () => {
    if (!draft) return;
    const profileConflicts = profileSectionConflicts(draft);
    if (profileConflicts.length) {
      notify(
        `Resolve the global profile sections before saving: ${profileConflictMessage(profileConflicts)}. Move variables so each section uses one profile.`,
      );
      return;
    }
    if (!beginDocumentOperation("publish")) return;
    try {
      const saved = dirty
        ? await repository.save(tenantId, draft, draft.revision)
        : draft;
      setDraft(saved);
      setDirty(false);
      cacheProjects([saved]);
      await syncProject(saved);
      if (transport.demo)
        await api(`/projects/${encodeURIComponent(saved.id)}/publish`, {
          method: "POST",
        });
      const version = await repository.publish(tenantId, saved.id);
      setDraft({ ...saved, status: "published" });
      const published = await repository.get(tenantId, saved.id);
      if (published) {
        setDraft(published);
        cacheProjects([published]);
      }
      notify(
        `Published version ${version.version}. The snapshot is immutable.`,
      );
    } catch (e) {
      if ((e as { status?: number }).status === 409) setConflict(true);
      notify((e as Error).message);
    } finally {
      finishDocumentOperation();
    }
  };
  async function syncProject(project: Project): Promise<void> {
    if (!transport.demo) return;
    let existing: Project | undefined;
    try {
      existing = await api<Project>(
        `/projects/${encodeURIComponent(project.id)}`,
      );
    } catch (e) {
      if ((e as Error).message !== "Project not found") throw e;
    }
    await api(`/projects/${encodeURIComponent(project.id)}`, {
      method: "PUT",
      body: JSON.stringify({
        project,
        expectedRevision: existing?.revision || 0,
      }),
    });
  }
  // Unsaved edits are kept as a saved draft before the editor moves away.
  const saveBeforeLeaving = async () => {
    if (documentOperation.current)
      throw Error("Wait for the current save or publish operation to finish.");
    if (!dirty || !draft) return;
    const saved = await repository.save(tenantId, draft, draft.revision);
    setDraft(saved);
    setDirty(false);
    setConflict(false);
    cacheProjects([saved]);
  };
  const scopeLeave = useRef(saveBeforeLeaving);
  scopeLeave.current = saveBeforeLeaving;
  useEffect(
    () => registerBeforeLeave?.(() => scopeLeave.current()),
    [registerBeforeLeave],
  );
  const openProject = async (id: string, view = "overview") => {
    if (switching.current) return;
    // Reopening the current document must not save a revision behind the editor.
    if (id === selectedId && inProject && draft) {
      setInProject(true);
      setModule(view, true);
      return;
    }
    switching.current = true;
    try {
      await saveBeforeLeaving();
      const project = await repository.get(tenantId, id);
      if (!project)
        throw new Error(
          "This project is unavailable or you do not have access.",
        );
      cacheProjects([project]);
      if (draft?.id === id) setDraft(structuredClone(project));
      setSelected(id);
      setInProject(true);
      setModule(view, true);
    } catch (error) {
      if ((error as { status?: number }).status === 409) setConflict(true);
      notify((error as Error).message);
    } finally {
      switching.current = false;
    }
  };
  const showProjects = async () => {
    if (switching.current) return;
    switching.current = true;
    try {
      await saveBeforeLeaving();
      setInProject(false);
      setModule("projects", false);
    } catch (error) {
      if ((error as { status?: number }).status === 409) setConflict(true);
      notify((error as Error).message);
    } finally {
      switching.current = false;
    }
  };
  const applyPageLink = async (save = true) => {
    if (documentOperation.current) {
      window.history.replaceState(null, "", documentOperation.current.url);
      return;
    }
    const link = readPageLink();
    if (!link) {
      setLinkReady(true);
      return;
    }
    if (linkNavigating.current) return;
    linkNavigating.current = true;
    try {
      if (save) await saveBeforeLeaving();
      if (link.tenant && link.tenant !== tenantId) {
        notify(
          "Select the site and company for this link to open the linked page.",
        );
        return;
      }
      const validSection =
        projectModules.some((page) => page.id === link.section) ||
        Object.hasOwn(workspacePages, link.section) ||
        ["settings", "outputs", "resources"].includes(link.section) ||
        resourcePages.some((page) =>
          [`library-${page.key}`, `resources-${page.key}`].includes(
            link.section,
          ),
        );
      if (!validSection || !allowed(link.section)) {
        notify("This linked page is not available for this account.");
        return;
      }
      if (
        link.project &&
        !projects.some((project) => project.id === link.project)
      ) {
        const linked = await repository.get(tenantId, link.project);
        if (!linked) {
          setInProject(false);
          setModuleState("projects");
          notify(
            "The linked project is unavailable or you do not have access.",
          );
          return;
        }
        cacheProjects([linked]);
      }
      if (isProjectModule(link.section) && !link.project) {
        notify("This page link requires a project.");
        return;
      }
      setupChecked.current = true;
      pendingPart.current = link.project !== draft?.id ? link.part : undefined;
      if (link.project) setSelected(link.project);
      setPart(link.part || "");
      setInProject(!!link.project);
      setModuleState(link.section);
    } catch (error) {
      notify((error as Error).message);
    } finally {
      linkNavigating.current = false;
      setLinkReady(true);
    }
  };
  const pageLinkNavigation = useRef(applyPageLink);
  pageLinkNavigation.current = applyPageLink;
  useEffect(() => {
    if (!loading && !loadError && !linkReady)
      void pageLinkNavigation.current(false);
  }, [loading, loadError, linkReady]);
  useEffect(() => {
    const navigate = () => {
      void pageLinkNavigation.current();
    };
    window.addEventListener("popstate", navigate);
    window.addEventListener("hashchange", navigate);
    return () => {
      window.removeEventListener("popstate", navigate);
      window.removeEventListener("hashchange", navigate);
    };
  }, []);
  useEffect(() => {
    if (!linkReady || loading || linkNavigating.current) return;
    if (readPageLink()?.tenant && readPageLink()?.tenant !== tenantId) return;
    const hash = pageLinkHash({
      section: module,
      project: inProject ? selectedId : undefined,
      part,
      tenant: tenantId,
    });
    if (location.hash === hash) return;
    const url = location.pathname + location.search + hash;
    if (readPageLink()) window.history.pushState(null, "", url);
    else window.history.replaceState(null, "", url);
  }, [linkReady, loading, module, inProject, selectedId, part]);
  const undo = () => {
    if (!history.length || !draft) return;
    const previous = history[history.length - 1];
    setHistory(history.slice(0, -1));
    setFuture([draft, ...future]);
    setDraft(previous);
    setDirty(true);
  };
  const redo = () => {
    if (!future.length || !draft) return;
    setHistory([...history, draft]);
    setDraft(future[0]);
    setFuture(future.slice(1));
    setDirty(true);
  };
  const newProject = async (starter?: Project) => {
    if (!transport.can("tenant.documents.manage")) {
      notify("Document creation is not available for this account.");
      return;
    }
    try {
      const name = title.trim() || starter?.name || "Untitled project";
      const p = starter
        ? {
            ...structuredClone(starter),
            id: uid("project"),
            name,
            revision: 0,
            status: "draft" as const,
          }
        : {
            schemaVersion: 1 as const,
            id: uid("project"),
            tenantId,
            name,
            fields: [],
            pages: [{ ...newPage(), backgroundMode: "inherit" }],
            outputs: createOutputProfiles(),
          };
      const saved = await repository.create(tenantId, parseProject(p), {
        useDefaults: !starter,
      });
      cacheProjects([saved]);
      setCatalogRevision((value) => value + 1);
      setSelected(saved.id);
      setInProject(true);
      setDialog("");
      setTitle("");
      setModule("configuration", true);
    } catch (error) {
      notify((error as Error).message);
    }
  };
  /** Ask before an import overwrites projects that already exist. */
  const resolveConflicts = async (imported: Project[]) => {
    const existing = await Promise.all(
      imported.map((project) => repository.get(tenantId, project.id)),
    );
    const names = existing
      .filter((project): project is Project => !!project)
      .map((project) => project.name);
    if (!names.length) return imported;
    const choice = await new Promise<"replace" | "copy" | "cancel">((resolve) =>
      setReplaceRequest({ names, resolve }),
    );
    setReplaceRequest(undefined);
    if (choice === "cancel")
      throw new Error("Import cancelled. Nothing was changed.");
    if (choice === "replace") return imported;
    return imported.map((project, index) =>
      existing[index]
        ? { ...project, id: uid("project"), name: `${project.name} (imported)` }
        : project,
    );
  };
  const importProjects = async (incoming: Project[], activate = true) => {
    // Batch archive imports review their selection in the archive dialog.
    const imported = activate ? await resolveConflicts(incoming) : incoming;
    const savedImports: Project[] = [];
    for (let raw of imported) {
      raw = migrateOutputProfiles(raw);
      if (transport.demo && transport.can("tenant.resources.manage")) {
        const available = await repository.resources(tenantId);
        raw = await retainOutputPresets(raw, available, (resource) =>
          repository.saveResource(tenantId, resource, 0),
        );
        raw = await retainProductionPresets(raw, available, (resource) =>
          repository.saveResource(tenantId, resource, 0),
        );
      }
      const current = await repository.get(tenantId, raw.id);
      savedImports.push(
        await repository.save(
          tenantId,
          { ...raw, tenantId },
          current?.revision || 0,
        ),
      );
    }
    cacheProjects(savedImports);
    setCatalogRevision((value) => value + 1);
    if (!activate) return;
    setDraft(undefined);
    setSelected(imported[0].id);
    setInProject(true);
    setModule("configuration", true);
  };
  const onValues = (v: Values) => {
    setValues(v);
    if (draft && transport.demo)
      localStorage.setItem(`proof-values-${draft.id}`, JSON.stringify(v));
  };
  const openHistory = async () => {
    if (!draft) return;
    setRevisions(await repository.history(tenantId, draft.id));
    setDialog("history");
  };
  const launchImpersonation = (previewSubject: string | null) => {
    if (!draft) return;
    setChoosingPreviewUser(false);
    if (!transport.demo) {
      if (!previewSelection || previewSelection.projectId !== draft.id) {
        setModule("proof");
        notify(
          "Select a published version in Live proof, then open Impersonation.",
        );
        return;
      }
      try {
        transport.openPreview({
          ...previewSelection,
          values,
          partId: part,
          sourceParameters,
          previewSubject,
        });
      } catch (error) {
        setChoosingPreviewUser(true);
        notify((error as Error).message);
      }
      return;
    }
    try {
      const url = prepareImpersonation({
        sourceParameters,
        project: draft,
        values,
        partId: part,
        demoProfile,
        previewSubject,
      });
      const popup = window.open("about:blank", "_blank");
      if (!popup) {
        setChoosingPreviewUser(true);
        notify("Allow popups, then choose Open proofing page to continue.");
        return;
      }
      popup.opener = null;
      popup.location.replace(url);
    } catch {
      notify(
        "Could not open impersonation. Check that browser storage is available.",
      );
    }
  };
  launchPreview.current = launchImpersonation;
  const openImpersonation = () => {
    if (!draft) return;
    previewOpener.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    previewLoad.current?.abort();
    const controller = new AbortController();
    previewLoad.current = controller;
    setCheckingPreviewUsers(true);
    void transport.client
      .previewUsers(controller.signal)
      .then((users) => {
        if (controller.signal.aborted) return;
        setPreviewUsers(users);
        setCheckingPreviewUsers(false);
        if (users.length) setChoosingPreviewUser(true);
        else launchPreview.current?.(null);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setCheckingPreviewUsers(false);
        notify(
          "Could not load available users. Click Impersonation to try again.",
        );
      });
  };
  const navigateHelp = (id: HelpActionId) => {
    const action = helpActions[id];
    if (
      action.kind === "dialog" &&
      ((["new", "import-document"].includes(action.dialog) &&
        !transport.can("tenant.documents.manage")) ||
        (action.dialog === "import-document" &&
          !transport.can("tenant.assets.manage")))
    ) {
      notify("This workspace action is not available for this account.");
      return;
    }
    if (action.kind === "module") setModule(action.module);
    else if (action.kind === "impersonation") openImpersonation();
    else if (action.dialog === "history") {
      if (!inProject) setModule("overview");
      else
        void openHistory().catch((error) => notify((error as Error).message));
    } else setDialog(action.dialog);
  };
  const authoringServices = useMemo(
    () => ({
      ...transport.authoring,
      listFormProjects: async (query: string, signal: AbortSignal) => {
        const page = repository.page
          ? await repository.page(tenantId, {
              search: query,
              limit: 100,
              status: "active",
            })
          : {
              items: (await repository.list(tenantId)).filter((row) =>
                row.name.toLowerCase().includes(query.toLowerCase()),
              ),
            };
        signal.throwIfAborted();
        return page.items.map((row) => ({ id: row.id, name: row.name }));
      },
      loadFormProject: async (id: string, signal: AbortSignal) => {
        const project = await repository.get(tenantId, id);
        signal.throwIfAborted();
        return project;
      },
      listFormProfiles: async (
        query: string,
        signal: AbortSignal,
        context?: Project,
      ) => {
        const resources = await repository.resources(tenantId, {
          scope: "shared",
        });
        const rows: { id: string; name: string; values: Values }[] = [];
        for (const resource of [
          ...new Map(
            resources
              .filter(
                (row) =>
                  row.kind === "form" &&
                  !row.archived &&
                  (row.value as any).purpose === "profile-collection",
              )
              .sort((a, b) => a.version - b.version)
              .map((row) => [row.id, row]),
          ).values(),
        ]) {
          if (signal.aborted) throw new DOMException("Aborted", "AbortError");
          const data = await transport.authoring.profileDataset?.(resource);
          const inputs = (resource.value as any)
            .fields as import("@proof/core").Field[];
          for (const record of data?.records || [])
            if (
              record.active &&
              record.name.toLowerCase().includes(query.toLowerCase())
            ) {
              const values: Values = {};
              for (const [id, value] of Object.entries(record.values)) {
                const input = inputs.find((field) => field.id === id);
                const target = context?.fields.find(
                  (field) => field.key === input?.key || field.id === id,
                );
                if (target) values[target.id] = value;
              }
              rows.push({
                id: record.id,
                name: `${resource.name} / ${record.name}`,
                values,
              });
            }
        }
        return rows;
      },

      listAssets: (signal?: AbortSignal) =>
        transport.client.listAssets(signal, draft?.id),
      upload: (
        file: File,
        _field?: import("@proof/core").Field,
        signal?: AbortSignal,
      ) => transport.client.uploadAsset(file, file.name, signal, draft?.id),
      listResources: () =>
        repository.resources(
          tenantId,
          draft
            ? { projectId: draft.id, scope: "available" }
            : { scope: "shared" },
        ),
      saveResource: transport.can("tenant.resources.manage")
        ? (resource: import("@proof/core").Resource) =>
            repository.saveResource(
              tenantId,
              resource.kind === "output"
                ? {
                    ...resource,
                    projectId: undefined,
                    value: globalOutputSettings(
                      resource.value as import("@proof/core").OutputProfile,
                    ),
                  }
                : resource.kind === "form"
                  ? { ...resource, projectId: undefined }
                  : { ...resource, ...(draft ? { projectId: draft.id } : {}) },
              resource.version,
            )
        : undefined,
    }),
    [transport, repository, tenantId, draft?.id],
  );
  const editorProps = draft
    ? { project: draft, onChange: change, notify }
    : null;
  const projectPage = inProject && isProjectModule(module);
  const resourceName = (key: string) =>
    key === "catalog"
      ? "All building blocks"
      : (projectPage ? projectResourcePages : resourcePages).find(
          (page) => page.key === key,
        )?.name || key;
  // Workspace › Projects › Project › Page, or Workspace › Section › Page.
  const crumbs: { label: string; go?: () => void }[] = [{ label: "Workspace" }];
  if (projectPage && draft) {
    crumbs.push({ label: "Projects", go: () => void showProjects() });
    crumbs.push({ label: draft.name, go: () => setModule("overview") });
    if (
      module === "resources" ||
      module.startsWith("resources-") ||
      module === "outputs"
    ) {
      crumbs.push({
        label: "Project configuration",
        go: allowed("resources") ? () => setModule("resources") : undefined,
      });
      if (module !== "resources")
        crumbs.push({ label: resourceName(resourceKey(module)) });
    } else if (module !== "overview")
      crumbs.push({
        label:
          projectModules.find((m) => m.id === module)?.name ||
          "Project settings",
      });
  } else if (module === "library" || module.startsWith("library-")) {
    crumbs.push({
      label: "Configuration",
      go: allowed("library") ? () => setModule("library") : undefined,
    });
    if (module !== "library")
      crumbs.push({ label: resourceName(resourceKey(module)) });
  } else crumbs.push({ label: workspacePages[module] || "Projects" });
  const workspaceLocation = (
    <>
      <nav className="breadcrumb" aria-label="Breadcrumb">
        {crumbs.map((crumb, index) => (
          <Fragment key={index}>
            {index > 0 && <ChevronRight size={13} aria-hidden="true" />}
            {index === crumbs.length - 1 ? (
              <strong aria-current="page">{crumb.label}</strong>
            ) : crumb.go ? (
              <a
                className="breadcrumb-link"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  crumb.go!();
                }}
              >
                {crumb.label}
              </a>
            ) : (
              <span>{crumb.label}</span>
            )}
          </Fragment>
        ))}
      </nav>
    </>
  );
  return (
    <AuthoringServicesProvider services={authoringServices}>
      {designerTry && (
        <DesignerTryItOut
          snapshot={designerTry}
          repository={repository}
          transport={transport}
          onClose={() => {
            setDesignerTry(undefined);
            previewOpener.current?.focus();
          }}
        />
      )}
      {documentOperationKind && (
        <DocumentOperationProgress operation={documentOperationKind} />
      )}
      {choosingPreviewUser && previewUsers && (
        <ImpersonationPicker
          users={previewUsers}
          onSelect={launchImpersonation}
          onClose={() => {
            setChoosingPreviewUser(false);
            previewOpener.current?.focus();
          }}
        />
      )}
      <ModuleThemeStyle appearance={appearance} />
      <div className="app-shell">
        <aside className="app-sidebar">
          {companySelector || (
            <>
              <a
                className="brand"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (allowed("projects")) void showProjects();
                }}
              >
                <span className="brand-mark">
                  <Layers size={24} />
                </span>
                <span>
                  proof<span className="brand-light">studio</span>
                  <small>DOCUMENT WORKSPACE</small>
                </span>
              </a>
              <button
                className="workspace-switch"
                onClick={() =>
                  notify(
                    transport.demo
                      ? "Demo workspace · all data stays in this browser and the local renderer."
                      : `Authenticated workspace · ${tenantId}`,
                  )
                }
              >
                <span className="workspace-avatar">
                  {transport.demo ? "F" : tenantId.slice(0, 2).toUpperCase()}
                </span>
                <span>
                  <strong>
                    {transport.demo ? "Fern & Form" : "Workspace"}
                  </strong>
                  <small>{transport.demo ? "Demo workspace" : tenantId}</small>
                </span>
                <ChevronDown size={14} />
              </button>
            </>
          )}
          <div className="sidebar-section-label">WORKSPACE</div>
          <nav className="app-navigation" aria-label="Workspace">
            {allowed("projects") && (
              <>
                <div className="projects-heading">
                  <button
                    className={`projects-home-button ${module === "projects" ? "active" : ""}`}
                    aria-label="Projects"
                    title="Projects"
                    aria-current={module === "projects" ? "page" : undefined}
                    onClick={() => {
                      setProjectsOpen(false);
                      void showProjects();
                    }}
                  >
                    <FolderOpen size={18} />
                    <span>Projects</span>
                  </button>
                  <button
                    ref={projectsButton}
                    className="projects-toggle"
                    aria-label={
                      projectsOpen
                        ? "Collapse recent projects"
                        : "Expand recent projects"
                    }
                    title={
                      projectsOpen
                        ? "Collapse recent projects"
                        : "Expand recent projects"
                    }
                    aria-controls="workspace-project-navigation"
                    aria-expanded={projectsOpen}
                    onClick={() => setProjectsOpen((open) => !open)}
                  >
                    <ChevronDown className="projects-chevron" size={14} />
                  </button>
                </div>
                <ResourceMenu
                  id="workspace-project-navigation"
                  label="Recent projects"
                  menuRef={projectsMenu}
                  open={projectsOpen}
                  pages={recentProjects.map((project) => ({
                    key: project.id,
                    name: project.name,
                  }))}
                  active={(id) => inProject && id === selectedId}
                  onClose={() => {
                    setProjectsOpen(false);
                    projectsButton.current?.focus();
                  }}
                  onSelect={(id) => {
                    fromRecentProjects.current = !compactSidebar;
                    void openProject(id);
                    if (compactSidebar) {
                      setProjectsOpen(false);
                      projectsButton.current?.focus();
                    }
                  }}
                />
              </>
            )}
            {inProject && draft && (
              <div
                className="project-nav"
                role="group"
                aria-label={`Project: ${draft.name}`}
              >
                <div className="project-nav-heading">
                  <span className="project-dot" />
                  <strong title={draft.name}>{draft.name}</strong>
                  <button
                    className="project-close"
                    aria-label="Close project"
                    title="Close project and return to all projects"
                    onClick={() => void showProjects()}
                  >
                    <X size={14} />
                  </button>
                </div>
                {projectModules
                  .filter((item) => allowed(item.id))
                  .map(({ id, name, Icon }) => (
                    <Fragment key={id}>
                      <button
                        aria-label={name}
                        title={name}
                        className={module === id ? "active" : ""}
                        aria-current={module === id ? "page" : undefined}
                        onClick={() => setModule(id)}
                      >
                        <Icon size={18} />
                        <span>{name}</span>
                        {id === "proof" && <span className="nav-live-dot" />}
                      </button>
                      {id === "proof" && (
                        <button
                          className="impersonation-nav"
                          aria-label="Impersonation"
                          title={
                            transport.demo
                              ? "Open impersonation in a new tab"
                              : "Choose a user and open a published proof preview in a new tab"
                          }
                          disabled={
                            checkingPreviewUsers ||
                            (!transport.demo &&
                              (!previewSelection ||
                                previewSelection.projectId !== draft.id))
                          }
                          onClick={openImpersonation}
                          aria-busy={checkingPreviewUsers}
                        >
                          <span>Impersonation</span>
                          <ArrowUpRight size={13} />
                        </button>
                      )}
                    </Fragment>
                  ))}
                {projectResourcePages.some((page) =>
                  allowed(projectResource(page.key)),
                ) && (
                  <>
                    <button
                      ref={configurationButton}
                      aria-label="Project configuration"
                      title="Project configuration"
                      aria-controls="workspace-configuration-navigation"
                      className={
                        module.startsWith("resources") || module === "outputs"
                          ? "active"
                          : ""
                      }
                      aria-expanded={resourcesOpen}
                      onClick={() => {
                        // Desktop: go to the section; collapse only from within it.
                        const inside =
                          module.startsWith("resources") ||
                          module === "outputs";
                        if (!compactSidebar && resourcesOpen && !inside) {
                          setModule(
                            allowed("resources")
                              ? "resources"
                              : projectResource(
                                  projectResourcePages.find((page) =>
                                    allowed(projectResource(page.key)),
                                  )!.key,
                                ),
                          );
                          return;
                        }
                        if (!compactSidebar && !resourcesOpen)
                          setModule(
                            allowed("resources")
                              ? "resources"
                              : projectResource(
                                  projectResourcePages.find((page) =>
                                    allowed(projectResource(page.key)),
                                  )!.key,
                                ),
                          );
                        setResourcesOpen((open) => !open);
                      }}
                    >
                      <Boxes size={18} />
                      <span>Project configuration</span>
                      <ChevronDown
                        className="configuration-chevron"
                        size={13}
                      />
                    </button>
                    <ResourceMenu
                      id="workspace-configuration-navigation"
                      label="Project configuration"
                      menuRef={configurationMenu}
                      open={resourcesOpen}
                      pages={projectResourcePages.filter((page) =>
                        allowed(projectResource(page.key)),
                      )}
                      active={(key) => module === projectResource(key)}
                      onClose={() => {
                        setResourcesOpen(false);
                        configurationButton.current?.focus();
                      }}
                      onSelect={(key) => {
                        setModule(projectResource(key));
                        if (compactSidebar) {
                          setResourcesOpen(false);
                          configurationButton.current?.focus();
                        }
                      }}
                    />
                  </>
                )}
                {allowed("settings") && (
                  <button
                    aria-label="Project settings"
                    title="Project settings"
                    className={module === "settings" ? "active" : ""}
                    aria-current={module === "settings" ? "page" : undefined}
                    onClick={() => setModule("settings")}
                  >
                    <Settings2 size={18} />
                    <span>Project settings</span>
                  </button>
                )}
              </div>
            )}
            {allowed("profiles") && (
              <button
                aria-label="Your profiles"
                title="Your profiles"
                className={module === "profiles" ? "active" : ""}
                aria-current={module === "profiles" ? "page" : undefined}
                onClick={() => setModule("profiles")}
              >
                <UserRound size={18} />
                <span>Your profiles</span>
              </button>
            )}
          </nav>
          <div className="sidebar-bottom">
            <nav
              className="app-navigation sidebar-configuration"
              aria-label="Configuration tools"
            >
              {resourcePages.some((page) => allowed(`library-${page.key}`)) && (
                <>
                  <button
                    ref={libraryButton}
                    aria-label="Configuration"
                    title="Configuration"
                    aria-controls="workspace-library-navigation"
                    className={module.startsWith("library") ? "active" : ""}
                    aria-expanded={libraryOpen}
                    onClick={() => {
                      if (
                        !compactSidebar &&
                        libraryOpen &&
                        !module.startsWith("library")
                      ) {
                        setModule(
                          allowed("library")
                            ? "library"
                            : `library-${resourcePages.find((page) => allowed(`library-${page.key}`))!.key}`,
                        );
                        return;
                      }
                      if (!compactSidebar && !libraryOpen)
                        setModule(
                          allowed("library")
                            ? "library"
                            : `library-${resourcePages.find((page) => allowed(`library-${page.key}`))!.key}`,
                        );
                      setLibraryOpen((open) => !open);
                    }}
                  >
                    <LibraryBig size={18} />
                    <span>Configuration</span>
                    <ChevronDown className="configuration-chevron" size={13} />
                  </button>
                  <ResourceMenu
                    id="workspace-library-navigation"
                    label="Configuration"
                    menuRef={libraryMenu}
                    open={libraryOpen}
                    pages={resourcePages.filter((page) =>
                      allowed(`library-${page.key}`),
                    )}
                    active={(key) => module === `library-${key}`}
                    onClose={() => {
                      setLibraryOpen(false);
                      libraryButton.current?.focus();
                    }}
                    onSelect={(key) => {
                      setModule(`library-${key}`);
                      if (compactSidebar) {
                        setLibraryOpen(false);
                        libraryButton.current?.focus();
                      }
                    }}
                  />
                </>
              )}
            </nav>
            {setupView && (
              <button
                className={module === "access" ? "active" : ""}
                onClick={() => setModule("access")}
              >
                <Users size={17} />
                Users & roles
              </button>
            )}
            {setupView && (
              <button
                className={module === "setup" ? "active" : ""}
                onClick={() => setModule("setup")}
              >
                <Settings2 size={17} />
                Workspace setup
              </button>
            )}
            {allowed("operations") && (
              <button
                className={module === "operations" ? "active" : ""}
                onClick={() => setModule("operations")}
              >
                <Server size={17} />
                Render operations
              </button>
            )}
            <button
              className={module === "help" ? "active" : ""}
              onClick={() => setModule("help")}
            >
              <BookOpen size={17} />
              Guides & help
            </button>
            <DisplayModeToggle fallback={resolveModuleTheme(appearance).mode} />
            {transport.demo && (
              <div className="demo-status">
                <span className="live-dot" />
                <span>
                  Demo mode <small>No database needed</small>
                </span>
              </div>
            )}
          </div>
          <nav className="sidebar-account" aria-label="Account navigation">
            {workspaceAccount || (
              <AccountMenu
                name={
                  transport.demo
                    ? "Alex Morgan"
                    : displayName || transport.principal?.subject || "Account"
                }
                detail={
                  transport.demo
                    ? "Workspace administrator"
                    : "Authenticated tenant access"
                }
                items={[
                  {
                    label: "Workspace",
                    Icon: LayoutGrid,
                    current: true,
                    onSelect: () => void showProjects(),
                  },
                ]}
              />
            )}
          </nav>
        </aside>
        <div className="app-main">
          {breadcrumbHost && createPortal(workspaceLocation, breadcrumbHost)}
          {(!breadcrumbHost || (projectPage && draft)) && (
            <header className="app-topbar">
              {!breadcrumbHost && workspaceLocation}
              {projectPage && draft && (
                <div className="topbar-actions">
                  <button
                    className="icon-button"
                    aria-label="Import revision"
                    title="Import revision"
                    disabled={
                      !transport.can("tenant.documents.manage") ||
                      !transport.can("tenant.assets.manage")
                    }
                    onClick={() => setDialog("revision-import")}
                  >
                    <Upload size={17} />
                  </button>
                  <span className={`save-status ${dirty ? "unsaved" : ""}`}>
                    <span />
                    {dirty ? "Unsaved changes" : "All changes saved"}
                  </span>
                  <button
                    className="icon-button"
                    aria-label="Draft history"
                    title="Draft history"
                    onClick={() =>
                      void openHistory().catch((error) => notify(error.message))
                    }
                  >
                    <History size={17} />
                  </button>
                  <Button
                    onClick={() => setDialog("export-project")}
                    disabled={
                      !transport.can("tenant.documents.manage") ||
                      !transport.can("tenant.assets.manage")
                    }
                  >
                    <Download size={14} />
                    Export project
                  </Button>
                  <Button
                    onClick={() => void save()}
                    disabled={
                      !!documentOperationKind ||
                      !dirty ||
                      !transport.can("tenant.documents.manage")
                    }
                  >
                    <Save size={14} />
                    Save draft
                  </Button>
                  {transport.can("tenant.templates.publish") && (
                    <Button
                      kind="dark"
                      disabled={!!documentOperationKind}
                      onClick={() => void publish()}
                    >
                      Publish
                      <ArrowUpRight size={14} />
                    </Button>
                  )}
                </div>
              )}
            </header>
          )}
          {conflict && draft && projectPage && (
            <div className="setup-warning" role="alert">
              This document changed elsewhere. Your unsaved edits have not been
              overwritten.{" "}
              <Button
                onClick={() => {
                  void repository
                    .get(tenantId, draft.id)
                    .then((saved) => {
                      if (saved) {
                        setDraft(saved);
                        setDirty(false);
                        setConflict(false);
                        setHistory([]);
                        setFuture([]);
                        setProjects((current) =>
                          current.map((row) =>
                            row.id === saved.id ? saved : row,
                          ),
                        );
                      }
                    })
                    .catch((error) => notify(error.message));
                }}
              >
                Reload saved document
              </Button>
            </div>
          )}
          {setupView?.warning && module !== "setup" && (
            <div className="setup-warning" role="note">
              Workspace setup needs attention.{" "}
              <button onClick={() => setModule("setup")}>Review setup</button>
            </div>
          )}
          {loading ||
          (needsCatalog && !completeCatalog.current && !catalogFailure) ? (
            <div className="loading">Opening your workspace…</div>
          ) : needsCatalog && catalogFailure ? (
            <section className="panel padded">
              <p role="alert">{catalogFailure}</p>
              <Button onClick={() => setCatalogAttempt((value) => value + 1)}>
                Retry project catalog
              </Button>
            </section>
          ) : projectPage && draft && editorProps ? (
            <div
              className={`app-content module-${module}`}
              key={`${draft.id}-${module}`}
            >
              {module === "overview" && (
                <Overview
                  current={draft}
                  onRevision={() => setDialog("revision-import")}
                  onEdit={() => setModule("designer")}
                  onProof={() => setModule("proof")}
                  onConfig={() => setModule("configuration")}
                  onDetails={() => setModule("settings")}
                />
              )}
              {module === "summary" && (
                <ProjectSummary
                  project={draft}
                  onGo={setModule}
                  onExport={() => setDialog("export-project")}
                  onDetails={() => setModule("settings")}
                  canEdit={transport.can("tenant.documents.manage")}
                  canExport={
                    transport.can("tenant.documents.manage") &&
                    transport.can("tenant.assets.manage")
                  }
                  canProof={transport.can("tenant.proofs.use")}
                />
              )}
              {module === "configuration" && <Configuration {...editorProps} />}
              {module === "reusable-forms" && (
                <ReusableFormsModule {...editorProps} />
              )}
              {module === "designer" && (
                <Designer
                  {...editorProps}
                  onUndo={undo}
                  onRedo={redo}
                  canUndo={!!history.length}
                  canRedo={!!future.length}
                  onTryItOut={
                    transport.can("tenant.proofs.use")
                      ? (project) => {
                          previewOpener.current =
                            document.activeElement instanceof HTMLElement
                              ? document.activeElement
                              : null;
                          setDesignerTry({
                            project,
                            values: structuredClone(values),
                            partId: part,
                            demoProfile,
                            sourceParameters: structuredClone(sourceParameters),
                          });
                        }
                      : undefined
                  }
                />
              )}
              {module === "proof" &&
                (transport.demo ? (
                  <Proof
                    sourceParameters={sourceParameters}
                    onSourceParametersChange={setSourceParameters}
                    project={draft}
                    repository={repository}
                    values={values}
                    onValues={onValues}
                    partId={part}
                    onPart={setPart}
                    demoProfile={demoProfile}
                    onDemoProfile={setDemoProfile}
                    notify={notify}
                  />
                ) : (
                  <PublishedWorkspaceView
                    sourceParameters={sourceParameters}
                    onSourceParametersChange={setSourceParameters}
                    project={draft}
                    repository={repository}
                    mode="proof"
                    onPreviewSelection={setPreviewSelection}
                    values={values}
                    onValues={onValues}
                    partId={part}
                    onPart={setPart}
                    notify={notify}
                  />
                ))}
              {module === "outputs" && <Outputs {...editorProps} />}
              {module === "bulk" &&
                (transport.demo ? (
                  <Bulk project={draft} notify={notify} />
                ) : (
                  <PublishedWorkspaceView
                    project={draft}
                    repository={repository}
                    mode="bulk"
                    values={values}
                    onValues={onValues}
                    partId={part}
                    onPart={setPart}
                    notify={notify}
                  />
                ))}

              {module.startsWith("resources") && (
                <Resources
                  onImportProjects={(p) => importProjects(p)}
                  repository={repository}
                  project={draft}
                  onChange={change}
                  notify={notify}
                  section={resourceKey(module)}
                  pages={projectResourcePages.filter((page) =>
                    allowed(projectResource(page.key)),
                  )}
                  onSection={(key) => setModule(projectResource(key))}
                />
              )}
              {module === "settings" && (
                <ProjectSettings
                  project={draft}
                  onChange={change}
                  canEdit={transport.can("tenant.documents.manage")}
                  backupLabel={
                    transport.demo
                      ? "Download workspace backup"
                      : "Download project JSON"
                  }
                  onBackup={() =>
                    downloadJson(
                      repository instanceof BrowserRepository
                        ? repository.exportState()
                        : draft,
                      "proof-studio-workspace-backup.json",
                    )
                  }
                  restore={
                    repository instanceof BrowserRepository ? (
                      <WorkspaceRestore
                        repository={repository}
                        onRestored={async () => {
                          setDraft(undefined);
                          setProjects(await repository.list(tenantId));
                        }}
                        notify={notify}
                      />
                    ) : (
                      <JsonImportButton
                        label="Restore project JSON"
                        title="Restore a project"
                        description={
                          <p>
                            Choose a file saved with “Download project JSON”.
                            You'll be asked before an existing project is
                            replaced.
                          </p>
                        }
                        maxBytes={50_000_000}
                        inspect={(value) => {
                          const project = parseProject({
                            ...(value as object),
                            tenantId,
                          });
                          return {
                            title: project.name,
                            summary: [
                              {
                                label: "Pages",
                                value: String(project.pages.length),
                              },
                              {
                                label: "Fields",
                                value: String(project.fields.length),
                              },
                            ],
                            before: draft,
                            after: project,
                            confirmLabel: "Restore project",
                          };
                        }}
                        onConfirm={async (value) => {
                          await importProjects([
                            parseProject({ ...(value as object), tenantId }),
                          ]);
                          return "Project restored.";
                        }}
                        finish="close"
                      />
                    )
                  }
                />
              )}
            </div>
          ) : (
            <div className={`app-content module-${module}`} key={module}>
              {module === "profiles" ? (
                <ProfileCollections projects={projects} />
              ) : module === "help" ? (
                <Help onNavigate={navigateHelp} />
              ) : module === "access" ? (
                <TenantAccess
                  onSelfDemoted={() => {
                    onIdentityChanged?.();
                    setSetupView(undefined);
                    setModule(inProject ? "overview" : "projects");
                    notify(
                      "Roles saved. Your administrative access has changed.",
                    );
                  }}
                />
              ) : module === "setup" && setupView ? (
                <TenantSetup
                  view={setupView}
                  onChange={acceptSetupView}
                  onGo={setModule}
                  {...(inProject && draft
                    ? {}
                    : { defaultsLabel: "Create starter defaults" })}
                  onUseDefaults={(signal) =>
                    useDocumentDefaults(
                      inProject && draft ? draft : scratch,
                      repository,
                      transport,
                      { signal, onlyMissing: !(inProject && draft) },
                    )
                  }
                />
              ) : module === "operations" ? (
                <Operations notify={notify} />
              ) : module === "library-integrations" ||
                module === "integrations" ? (
                <LibraryIntegrations
                  projects={projects}
                  repository={repository}
                  tenantId={tenantId}
                  initialProjectId={selectedId}
                  onImport={(p) => importProjects(p)}
                  notify={notify}
                />
              ) : module === "library-appearance" ? (
                <AppearanceSettings
                  repository={repository}
                  tenantId={tenantId}
                  canEditSite={
                    transport.demo || transport.can("tenant.setup.manage")
                  }
                  notify={notify}
                  onSaved={setAppearance}
                />
              ) : module === "library-outputs" ? (
                <>
                  <OutputProfileMerge
                    repository={repository}
                    tenantId={tenantId}
                    notify={notify}
                    onMerged={() =>
                      setOutputCatalogRevision((value) => value + 1)
                    }
                  />
                  <Outputs
                    key={outputCatalogRevision}
                    project={scratch}
                    mode="library"
                    onChange={() =>
                      notify(
                        "Open a project to assign a global output profile.",
                      )
                    }
                    notify={notify}
                  />
                </>
              ) : module.startsWith("library") ? (
                <Resources
                  onImportProjects={(p) => importProjects(p)}
                  repository={repository}
                  project={scratch}
                  hasDocument={false}
                  onChange={() =>
                    notify("Open a project before applying this resource.")
                  }
                  notify={notify}
                  section={resourceKey(module)}
                  pages={resourcePages.filter((page) =>
                    allowed(`library-${page.key}`),
                  )}
                  onSection={(key) => setModule(`library-${key}`)}
                />
              ) : (
                <ProjectsHome
                  projects={projects}
                  repository={repository}
                  onPage={cacheProjects}
                  refreshToken={catalogRevision}
                  loadError={loadError}
                  onOpen={(id) => void openProject(id)}
                  onNew={() => setDialog("new")}
                  onImport={() => setDialog("import-document")}
                  onArchive={async (project, archived) => {
                    try {
                      const saved = await repository.save(
                        tenantId,
                        { ...project, status: archived ? "archived" : "draft" },
                        project.revision,
                      );
                      cacheProjects([saved]);
                      notify(
                        archived ? "Project archived" : "Project restored",
                      );
                    } catch (error) {
                      notify(
                        error instanceof Error
                          ? error.message
                          : "Project could not be updated",
                      );
                      throw error;
                    }
                  }}
                  onDelete={async (project) => {
                    await repository.remove(tenantId, project.id);
                    setProjects((current) =>
                      current.filter((row) => row.id !== project.id),
                    );
                  }}
                />
              )}
            </div>
          )}
        </div>
        {toast && (
          <div
            className="toast"
            role="status"
            data-tone={looksLikeError(toast) ? "error" : "success"}
          >
            {looksLikeError(toast) ? (
              <CircleAlert size={18} aria-hidden />
            ) : (
              <CheckCircle2 size={18} aria-hidden />
            )}
            <span>
              {looksLikeError(toast) ? friendlyMessage(toast) : toast}
            </span>
            <button
              aria-label="Dismiss notification"
              onClick={() => setToast("")}
            >
              <X size={16} />
            </button>
          </div>
        )}
        {replaceRequest && (
          <Modal
            title="This project already exists"
            onClose={() => replaceRequest.resolve("cancel")}
          >
            <div className="ps-import">
              <p>
                {replaceRequest.names.length === 1
                  ? `“${replaceRequest.names[0]}” is already in this workspace.`
                  : `${replaceRequest.names.length} of these projects are already in this workspace: ${replaceRequest.names.join(", ")}.`}{" "}
                Keep both to import a separate copy, or replace the existing
                version with the file.
              </p>
              <div className="ps-notice" data-tone="warning">
                <CircleAlert size={16} aria-hidden />
                <p>
                  Replacing overwrites the current design, fields and settings.
                </p>
              </div>
              <div className="ps-actions ps-actions-split">
                <Button onClick={() => replaceRequest.resolve("cancel")}>
                  Cancel
                </Button>
                <span className="ps-actions" style={{ marginTop: 0 }}>
                  <Button onClick={() => replaceRequest.resolve("replace")}>
                    Replace existing
                  </Button>
                  <Button
                    kind="primary"
                    onClick={() => replaceRequest.resolve("copy")}
                  >
                    Keep both
                  </Button>
                </span>
              </div>
            </div>
          </Modal>
        )}
        {dialog === "export-project" && draft && (
          <ProjectPackage project={draft} onClose={() => setDialog("")} />
        )}
        {dialog === "import-document" && (
          <DocumentImport
            onArchiveImport={async (project) => {
              await importProjects([project], false);
            }}
            onArchiveOpen={() => {
              setDialog("");
              void showProjects();
            }}
            onClose={() => setDialog("")}
            onImport={async (project) => {
              await importProjects([project]);
              setModule("designer", true);
            }}
          />
        )}
        {dialog === "revision-import" && draft && (
          <DocumentRevision
            project={draft}
            onClose={() => setDialog("")}
            onApply={(project) => {
              if (!transport.can("tenant.documents.manage"))
                throw new Error(
                  "Document editing is not available for this account.",
                );
              change(project);
              setDialog("");
              setModule("designer");
              notify(
                "Revision merged into the draft. Review the proof, then Save draft to retain the previous saved revision.",
              );
            }}
          />
        )}
        {dialog === "new" && (
          <Modal title="Create a project" onClose={() => setDialog("")} wide>
            <Control label="Project name">
              <input
                autoFocus
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Give your project a name"
              />
            </Control>
            <div className="starter-grid">
              <button
                className="starter-card"
                onClick={() => void newProject()}
              >
                <Plus size={32} />
                <h3>Blank document</h3>
                <p>Start with your own idea</p>
              </button>
              {(transport.demo ? demoProjects() : []).map((p) => (
                <button
                  className="starter-card"
                  key={p.id}
                  onClick={() => void newProject(p)}
                >
                  <div className="starter-preview">
                    <ProofCanvas
                      {...transport.proofServices}
                      project={p}
                      values={{}}
                      zoom={p.id === "business-card" ? 0.6 : 0.3}
                    />
                  </div>
                  <h3>{p.name}</h3>
                  <p>{p.description}</p>
                </button>
              ))}
            </div>
          </Modal>
        )}
        {dialog === "history" && (
          <Modal title="Draft history" onClose={() => setDialog("")}>
            <p className="muted">
              Restoring a revision creates a new draft. Published versions stay
              unchanged.
            </p>
            {revisionRows.length ? (
              revisionRows.map((r) => (
                <article className="saved-proof-row" key={r.id}>
                  <div>
                    <strong>Revision {r.revision}</strong>
                    <small>
                      {new Date(r.at).toLocaleString()} · {r.reason}
                      {r.manualLocked ? " · Manually locked" : ""}
                      {r.publicationLockedUntil &&
                      Date.parse(r.publicationLockedUntil) > Date.now()
                        ? ` · Publication protected until ${new Date(r.publicationLockedUntil).toLocaleDateString()}`
                        : ""}
                    </small>
                  </div>
                  <div className="button-row">
                    <Button
                      onClick={() =>
                        void repository
                          .lockRevision(
                            tenantId,
                            draft!.id,
                            r.id,
                            !r.manualLocked,
                          )
                          .then(() => repository.history(tenantId, draft!.id))
                          .then(setRevisions)
                          .catch((e) => notify(e.message))
                      }
                    >
                      {r.manualLocked ? "Unlock backup" : "Lock backup"}
                    </Button>
                    <Button
                      onClick={() =>
                        void repository
                          .restore(tenantId, draft!.id, r.id, draft!.revision)
                          .then((restored) => {
                            setDraft(restored);
                            setDirty(false);
                            cacheProjects([restored]);
                          })
                          .then(() => {
                            setDialog("");
                            notify("Revision restored into a new draft.");
                          })
                          .catch((e) => notify(e.message))
                      }
                    >
                      Restore
                    </Button>
                  </div>
                </article>
              ))
            ) : (
              <p>
                The next saved change archives this draft. Published source
                drafts are protected for 14 days.
              </p>
            )}
          </Modal>
        )}
      </div>
    </AuthoringServicesProvider>
  );
}
function Overview({
  current,
  onRevision,
  onEdit,
  onProof,
  onConfig,
  onDetails,
}: {
  current: Project;
  onRevision: () => void;
  onEdit: () => void;
  onProof: () => void;
  onConfig: () => void;
  onDetails: () => void;
}) {
  const transport = useWorkspaceTransport();
  // Fit the first page (and its tilt) to the preview pane.
  const visual = useRef<HTMLDivElement>(null);
  const [pane, setPane] = useState<{ width: number; height: number }>();
  useEffect(() => {
    const element = visual.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) =>
      setPane({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      }),
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const page = current.pages[0];
  const tilt = (7 * Math.PI) / 180;
  const fitZoom =
    pane && page.width && page.height
      ? Math.min(
          1.5,
          Math.max(
            0.15,
            Math.min(
              (pane.width * 0.82) /
                (page.width * Math.cos(tilt) + page.height * Math.sin(tilt)),
              // Leave room for the preview label along the bottom.
              ((pane.height - 56) * 0.82) /
                (page.width * Math.sin(tilt) + page.height * Math.cos(tilt)),
            ),
          ),
        )
      : page.width && page.width > 450
        ? 0.35
        : 0.5;
  return (
    <div className="overview">
      <section className="featured-project">
        <div className="featured-copy">
          <div className="featured-label">
            <span className="badge green">PROJECT</span>
            <span className="badge soft">
              {current.status === "published" ? "Published" : "Draft"}
            </span>
          </div>
          <h2>{current.name}</h2>
          <p>{current.description}</p>
          <div className="featured-stats">
            <span>
              <strong>{current.fields.length}</strong> field
              {current.fields.length === 1 ? "" : "s"}
            </span>
            <span>
              <strong>{current.pages.length}</strong> page
              {current.pages.length === 1 ? "" : "s"}
            </span>
            <span>
              <strong>{current.parts.length}</strong> part
              {current.parts.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="featured-actions">
            <Button
              disabled={
                !transport.can("tenant.documents.manage") ||
                !transport.can("tenant.assets.manage")
              }
              onClick={onRevision}
            >
              Import revision
            </Button>
            <Button kind="dark" onClick={onProof}>
              <ScanEye size={16} />
              Open live proof
              <ArrowUpRight size={15} />
            </Button>
            <Button
              disabled={!transport.can("tenant.documents.manage")}
              onClick={onEdit}
            >
              <PenTool size={15} />
              Edit design
            </Button>
          </div>
          <button
            className="text-button"
            disabled={!transport.can("tenant.documents.manage")}
            onClick={onDetails}
          >
            Edit project details <ChevronRight size={13} />
          </button>
        </div>
        <div className="featured-visual" ref={visual}>
          <div className="featured-line one" />
          <div className="featured-line two" />
          <div className="featured-paper">
            <ProofCanvas
              {...transport.proofServices}
              project={current}
              values={{}}
              zoom={Math.round(fitZoom * 100) / 100}
            />
          </div>
          <div className="preview-pill">
            <span className="live-dot" /> A live preview of your ideas
          </div>
        </div>
      </section>
      <div className="overview-quick-actions">
        <button
          disabled={!transport.can("tenant.documents.manage")}
          onClick={onConfig}
        >
          <span className="quick-icon lavender">
            <SlidersHorizontal size={21} />
          </span>
          <span>
            <strong>1 · Set up the form</strong>
            <small>Choose what people fill in</small>
          </span>
          <ArrowUpRight size={17} />
        </button>
        <button
          disabled={!transport.can("tenant.documents.manage")}
          onClick={onEdit}
        >
          <span className="quick-icon mint">
            <PenTool size={21} />
          </span>
          <span>
            <strong>2 · Design the document</strong>
            <small>Place text, images and artwork</small>
          </span>
          <ArrowUpRight size={17} />
        </button>
        <button onClick={onProof}>
          <span className="quick-icon peach">
            <ScanEye size={21} />
          </span>
          <span>
            <strong>3 · Proof and share</strong>
            <small>Try real details, then export</small>
          </span>
          <ArrowUpRight size={17} />
        </button>
      </div>
    </div>
  );
}
const resourceDescriptions: Record<string, string> = {
  assets: "Images, logos and artwork used in your designs.",
  fonts: "Typefaces available to designers and in proofs.",
  forms: "Reusable sets of form fields you can add to any project.",
  profiles:
    "Saved answers people can pick from, such as office addresses, with access by user, group, company or role.",
  datasets: "Lists and tables that feed dropdowns and lookups.",
  mapping:
    "Rules, presets, validation and formatting that shape how fields behave.",
  bleed: "Bleed, slug and printer's marks for print-ready pages.",
  sheets: "Sheet sizes, paper stock and imposition for production.",
  outputs: "How finished files are produced: PDF, images and more.",
  catalog:
    "Every saved building block in one list, including text styles and page sizes.",
  "profile-data":
    "The profile types people choose from on Global profiles, and the fields each type stores.",
  integrations:
    "Embeds, order handoff, data transforms and the API, for any project.",
  appearance: "Display mode and the look of the plug-in modules.",
};
function Resources({
  repository,
  project,
  onChange,
  notify,
  section = "all",
  hasDocument = true,
  pages,
  onSection,
  onImportProjects,
}: {
  onImportProjects: (projects: Project[]) => void | Promise<void>;
  hasDocument?: boolean;
  repository: Repository;
  project: Project;
  onChange: (p: Project) => void;
  notify: (s: string) => void;
  section?: string;
  pages: { key: string; name: string }[];
  onSection: (key: string) => void;
}) {
  const transport = useWorkspaceTransport();
  const [datasetView, setDatasetView] = useState("library");
  const title =
    section === "all"
      ? hasDocument
        ? "Project configuration"
        : "Configuration"
      : section === "catalog"
        ? "All building blocks"
        : (
            pages.find((page) => page.key === section) ||
            resourcePages.find((page) => page.key === section)
          )?.name || section;
  return (
    <div className="resources-page">
      <div className="page-intro">
        <div>
          <span className="eyebrow">
            {hasDocument
              ? `PROJECT CONFIGURATION · ${project.name.toUpperCase()}`
              : "SHARED ACROSS EVERY PROJECT"}
          </span>
          <h1>{title}</h1>
          {section !== "all" && <p>{resourceDescriptions[section]}</p>}
        </div>
      </div>
      {section === "all" && (
        <div className="resource-hub">
          {[...pages, { key: "catalog", name: "All building blocks" }].map(
            (page) => (
              <button
                key={page.key}
                className="resource-hub-card"
                onClick={() => onSection(page.key)}
              >
                <strong>{page.name}</strong>
                <small>{resourceDescriptions[page.key]}</small>
                <ArrowUpRight size={16} />
              </button>
            ),
          )}
        </div>
      )}
      {section === "profiles" &&
        (hasDocument ? (
          <ProjectProfiles
            project={project}
            onChange={onChange}
            notify={notify}
            canAssign={transport.can("tenant.documents.manage")}
          />
        ) : (
          <GlobalProfileGrid
            tenantId={project.tenantId}
            notify={notify}
            onConfigure={() => onSection("profile-data")}
          />
        ))}
      {section === "profile-data" && (
        <>
          <GlobalProfiles
            project={project}
            onChange={onChange}
            notify={notify}
            canAssign={false}
          />
        </>
      )}
      {["validation", "formatting"].includes(section) && (
        <section className="panel padded">
          <FieldPresetLibrary
            key={`${project.id}:${section}`}
            project={hasDocument ? project : undefined}
            kind={section === "validation" ? "validation" : "format"}
          />
        </section>
      )}
      {section === "variable-types" && (
        <section className="panel padded">
          <VariableTypeMapper
            project={hasDocument ? project : undefined}
            onChange={onChange}
          />
        </section>
      )}
      {section === "mapping" && (
        <ResourceMapping
          project={project}
          onChange={onChange}
          notify={notify}
        />
      )}
      {section === "datasets" && (
        <>
          <div className="section-tabs" aria-label="Dataset collections">
            <button
              className={datasetView === "library" ? "active" : ""}
              onClick={() => setDatasetView("library")}
            >
              Reusable dataset library
            </button>
            <button
              disabled={
                !hasDocument || !transport.can("tenant.documents.manage")
              }
              className={datasetView === "document" ? "active" : ""}
              onClick={() => setDatasetView("document")}
            >
              Document datasets (
              {new Set(project.datasets.map((d) => d.id)).size})
            </button>
          </div>
          <div hidden={datasetView !== "document"} className="panel padded">
            <DatasetManager
              key={project.id}
              project={project}
              onChange={onChange}
              notify={notify}
            />
          </div>
        </>
      )}
      {["catalog", "forms", "datasets", "mapping", "bleed", "sheets"].includes(
        section,
      ) && (
        <div hidden={section === "datasets" && datasetView !== "library"}>
          <section className="panel padded">
            <CatalogManager
              key={`${project.id}:${section}`}
              repository={repository}
              initialScope={
                hasDocument && ["bleed", "sheets"].includes(section)
                  ? "project"
                  : "shared"
              }
              allowProjectScope={hasDocument}
              permissions={{
                manage: transport.can("tenant.resources.manage"),
                apply: hasDocument && transport.can("tenant.documents.manage"),
                policy: transport.can("tenant.setup.manage"),
              }}
              project={project}
              onChange={onChange}
              notify={notify}
              kinds={
                section === "forms"
                  ? ["form"]
                  : section === "datasets"
                    ? ["dataset"]
                    : section === "mapping"
                      ? ["rule", "preset", "validation", "format"]
                      : section === "bleed"
                        ? ["production"]
                        : section === "sheets"
                          ? ["sheet"]
                          : undefined
              }
            />
          </section>
        </div>
      )}
      {["catalog", "assets"].includes(section) && (
        <AssetLibrary
          project={project}
          hasDocument={hasDocument}
          onChange={onChange}
          notify={notify}
        />
      )}
      {["catalog", "fonts"].includes(section) && (
        <FontLibrary
          project={project}
          hasDocument={hasDocument}
          onChange={onChange}
          notify={notify}
          canApply={hasDocument && transport.can("tenant.documents.manage")}
        />
      )}
    </div>
  );
}
function Operations({ notify }: { notify: (s: string) => void }) {
  const { api } = useWorkspaceTransport();
  const [jobs, setJobs] = useState<JobView[]>([]),
    [status, setStatus] = useState<Record<string, unknown>>({}),
    [reasons, setReasons] = useState<Record<string, string>>({});
  const mutateJob = (
    job: JobView,
    action: "retry" | "cancel",
    probation = false,
  ) => {
    void api<JobView>(`/render-operations/${job.id}/${action}`, {
      method: "POST",
      body: JSON.stringify({
        reason: reasons[job.id],
        stateVersion: job.stateVersion,
        probation,
      }),
    })
      .then((updated) => {
        setJobs((list) => list.map((j) => (j.id === updated.id ? updated : j)));
        setReasons((values) => ({ ...values, [job.id]: "" }));
        notify(
          action === "cancel"
            ? "Job cancelled."
            : "Replacement attempt queued.",
        );
      })
      .catch((error) => notify(error.message));
  };
  useEffect(() => {
    const refresh = () => {
      void api<JobView[]>("/render-operations")
        .then(setJobs)
        .catch((error) => notify(error.message));
      void api<Record<string, unknown>>("/status")
        .then(setStatus)
        .catch(() => {});
    };
    refresh();
    void api<Record<string, unknown>>("/status")
      .then(setStatus)
      .catch(() => {});
    const timer = setInterval(refresh, 1500);
    return () => clearInterval(timer);
  }, []);
  return (
    <div className="operations-page">
      <div className="page-intro">
        <div>
          <span className="eyebrow">KEEP AN EYE ON THE OUTPUT</span>
          <h1>Render operations</h1>
          <p>
            Immutable snapshots, attempt history, and artifact verification.
          </p>
        </div>
        <span className="badge green">
          <span className="live-dot" />
          {status.ready === false
            ? "Renderer needs attention"
            : "Renderer ready"}
        </span>
      </div>
      <p className="muted">
        {(status.queue as { durable?: boolean })?.durable
          ? "Snapshots and artifacts persist across restarts."
          : "Memory storage is active; jobs are retained for this server session."}
      </p>
      <div className="operation-stats">
        <section className="panel padded">
          <strong>{jobs.filter((j) => j.state === "complete").length}</strong>
          <span>Completed</span>
        </section>
        <section className="panel padded">
          <strong>
            {
              jobs.filter((j) => j.state === "queued" || j.state === "running")
                .length
            }
          </strong>
          <span>In progress</span>
        </section>
        <section className="panel padded">
          <strong>
            {
              jobs.filter(
                (j) => j.state === "failed" || j.state === "quarantined",
              ).length
            }
          </strong>
          <span>Needs attention</span>
        </section>
      </div>
      <section className="panel padded">
        <h3>Jobs</h3>
        {jobs.length ? (
          jobs.map((j) => (
            <details className="job-details" key={j.id}>
              <summary>
                <strong>{j.projectName}</strong>
                <span>{j.profileName}</span>
                <span
                  className={`badge ${j.state === "complete" ? "green" : j.state === "failed" ? "red" : ""}`}
                >
                  {j.state}
                </span>
              </summary>
              <p>
                {j.id} · attempts: {j.attempts}
              </p>
              {j.events.map((e, i) => (
                <div className="audit-event" key={i}>
                  <time>{new Date(e.at).toLocaleTimeString()}</time>
                  <span>{e.message}</span>
                </div>
              ))}
              {j.artifact && (
                <p className="hash">SHA-256: {j.artifact.sha256}</p>
              )}
              <ul className="attempt-history">
                {j.attemptRecords.map((attempt) => (
                  <li key={attempt.id}>
                    Attempt {attempt.generation}: {attempt.state}
                    {attempt.probation ? " (probation)" : ""} · {attempt.reason}
                  </li>
                ))}
              </ul>
              {["queued", "running", "failed", "quarantined"].includes(
                j.state,
              ) &&
                j.attempts < 4 && (
                  <>
                    <Control label={`Reason for ${j.projectName}`}>
                      <input
                        maxLength={500}
                        value={reasons[j.id] || ""}
                        onChange={(e) =>
                          setReasons((values) => ({
                            ...values,
                            [j.id]: e.target.value,
                          }))
                        }
                        placeholder="Explain the action after reviewing this job"
                      />
                    </Control>
                    {["queued", "running"].includes(j.state) ? (
                      <Button
                        disabled={!reasons[j.id]?.trim()}
                        onClick={() => mutateJob(j, "cancel")}
                      >
                        Cancel job
                      </Button>
                    ) : (
                      <Button
                        disabled={!reasons[j.id]?.trim()}
                        onClick={() => mutateJob(j, "retry", j.attempts >= 3)}
                      >
                        {j.attempts >= 3
                          ? "Grant final probation attempt"
                          : "Retry job"}
                      </Button>
                    )}
                  </>
                )}
              {j.artifact?.downloadAllowed && (
                <a className="button" href={`/api/jobs/${j.id}/artifact`}>
                  Download artifact
                </a>
              )}
            </details>
          ))
        ) : (
          <p className="muted">
            Generate a document to see its render history here.
          </p>
        )}
      </section>
      <section className="panel padded">
        <h3>Runtime capabilities</h3>
        <pre>{JSON.stringify(status, null, 2)}</pre>
        <p className="muted">
          Demo jobs run in process. Persistent distributed dispatch is supplied
          through a host adapter.
        </p>
      </section>
    </div>
  );
}
